/**
 * DeliverableChecklist — FE-derived "Definition of Done" widget.
 *
 * Mirrors the backend `get_deliverable_status` tool's output, computed
 * client-side from data already loaded into the ProcessViewer store
 * (model + lastRun). Backend authoritative version requires session
 * scratchpad + workspace state which isn't reachable via plain HTTP;
 * surfacing a derived view keeps PR-B self-contained.
 *
 * Items rendered:
 *   - Charter set
 *   - At least one VALIDATE node authored
 *   - Last run completed and passed (every validator pass)
 *   - validate_work(mode='full') has run (any node has reviews)
 *   - At least one OUTPUT node validated to APPROVED
 */

import React from 'react';
import { CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import type { ModelDefinition, ModelRun } from '../../types/modeling';
import { formatCount } from '../../utils/plural';

interface DeliverableChecklistProps {
  model: ModelDefinition;
  lastRun: ModelRun | null;
}

interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
  hint?: string;
  /** Stronger than "not done" — something is actively failing. */
  failing?: boolean;
}

function buildItems(model: ModelDefinition, lastRun: ModelRun | null): ChecklistItem[] {
  const charterSet = !!model.charter;

  const validateNodes = model.nodes.filter((n) => n.node_type === 'validate');

  const lastRunPassed =
    !!lastRun &&
    lastRun.status === 'completed' &&
    (lastRun.validation_results?.every((v) => v.passed) ?? true);

  const lastRunFailed = !!lastRun && (lastRun.status === 'failed' ||
    (lastRun.validation_results?.some((v) => !v.passed) ?? false));

  // Mirrors the backend `get_deliverable_status` check at
  // get_deliverable_status.py:84-87: governance review is "called"
  // when ANY node carries reviews OR the model has a typed
  // validation_outcome. The latter is set by validate_work approval
  // AND by accept_data_supply_deliverable on the data-supply path
  // (where execute_model's run-of-record is the validation outcome,
  // not an LLM critique pass).
  const validateWorkCalled =
    model.nodes.some(
      (n) => Array.isArray(n.reviews) && n.reviews.length > 0,
    ) || !!model.validation_outcome;

  const approvedOutputCount = model.nodes.filter(
    (n) => n.node_type === 'output' && n.validation_status === 'approved',
  ).length;

  const failedValidatorCount = (lastRun?.validation_results ?? []).filter(
    (v) => !v.passed,
  ).length;

  return [
    {
      id: 'charter',
      label: 'Model charter set',
      done: charterSet,
      hint: charterSet ? undefined : 'Call set_model_charter — required at every tier.',
    },
    {
      id: 'validators',
      label:
        validateNodes.length > 0
          ? `${formatCount(validateNodes.length, { one: 'validator node', other: 'validator nodes' })} authored`
          : 'At least one VALIDATE node',
      done: validateNodes.length > 0,
      hint:
        validateNodes.length > 0
          ? undefined
          : 'Add a VALIDATE node with a typed pass:BOOL output.',
    },
    {
      id: 'last_run',
      label: lastRun
        ? lastRunPassed
          ? `Last run passed (${lastRun.validation_results?.length ?? 0} checks)`
          : lastRunFailed
            ? `Last run failed (${formatCount(failedValidatorCount, { one: 'check', other: 'checks' })})`
            : 'Last run incomplete'
        : 'Run-of-record produced by execute_model',
      done: lastRunPassed,
      failing: lastRunFailed,
      hint: lastRun
        ? lastRunPassed
          ? undefined
          : 'Click Run model to retry once the failures are addressed.'
        : 'No run yet — click Run model to produce the first record.',
    },
    {
      id: 'validate_work',
      label: validateWorkCalled
        ? 'validate_work governance review run'
        : 'validate_work(mode="full") for governance review',
      done: validateWorkCalled,
      hint: validateWorkCalled
        ? undefined
        : 'Ask the agent to run validate_work to get LLM-critic sign-off.',
    },
    {
      id: 'delivered',
      label:
        approvedOutputCount > 0
          ? `${formatCount(approvedOutputCount, { one: 'OUTPUT artifact', other: 'OUTPUT artifacts' })} approved`
          : 'OUTPUT-node artifact validated to APPROVED',
      done: approvedOutputCount > 0,
      hint:
        approvedOutputCount > 0
          ? undefined
          : 'validate_work approval registers OUTPUT-node deliveries automatically.',
    },
  ];
}

export default function DeliverableChecklist({ model, lastRun }: DeliverableChecklistProps) {
  const items = buildItems(model, lastRun);
  const doneCount = items.filter((i) => i.done).length;
  const allDone = doneCount === items.length;

  return (
    <div className="w-[360px] bg-surface border border-border-default rounded-md shadow-bark-md p-3">
      <div className="flex items-center justify-between mb-2.5">
        <span className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em]">
          Definition of done
        </span>
        <span
          className={`font-mono text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded ${
            allDone
              ? 'bg-success-bg text-success'
              : 'bg-bg-warm text-fg-secondary'
          }`}
        >
          {doneCount}/{items.length}
        </span>
      </div>

      <ul className="space-y-1.5">
        {items.map((item) => {
          const Icon = item.done
            ? CheckCircle2
            : item.failing
              ? AlertCircle
              : Circle;
          const iconClass = item.done
            ? 'text-success'
            : item.failing
              ? 'text-danger'
              : 'text-fg-tertiary';
          return (
            <li key={item.id} className="flex items-start gap-2">
              <Icon
                className={`w-4 h-4 ${iconClass} flex-shrink-0 mt-px`}
                strokeWidth={1.5}
              />
              <div className="flex-1 min-w-0">
                <div
                  className={`text-[12.5px] leading-snug ${
                    item.done ? 'text-fg-secondary' : 'text-fg'
                  }`}
                >
                  {item.label}
                </div>
                {item.hint && !item.done && (
                  <div className="mt-0.5 text-[11px] text-fg-tertiary leading-snug">
                    {item.hint}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {!allDone && (
        <p className="mt-2.5 pt-2.5 border-t border-divider text-[11px] text-fg-tertiary leading-snug">
          A workspace is not done until both the run-of-record passes
          AND validate_work returns no rejections.
        </p>
      )}
    </div>
  );
}
